import { useRef, useCallback, useState } from 'react';

/**
 * A custom React hook that provides a resettable timer.
 *
 * This hook allows you to start, reset, and clear a timer that executes a callback after a specified delay.
 * It also provides a boolean flag indicating whether the timer is currently running.
 *
 * @param callback - The function to execute when the timer completes.
 * @param delay - The delay in milliseconds before the callback is executed.
 * @returns An object containing:
 *   - `start`: Function to start the timer.
 *   - `reset`: Function to reset (restart) the timer.
 *   - `clear`: Function to clear (stop) the timer.
 *   - `running`: Boolean indicating if the timer is currently running.
 */
export function useResettableTimer(callback: () => void, delay: number) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isRunning, setRunning] = useState(false);

  const runCallback = useCallback(() => {
    setRunning(false);
    callback();
  }, [callback]);

  const clear = useCallback(() => {
    if (timerRef.current) {
      setRunning(false);
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    timerRef.current = setTimeout(runCallback, delay);
    setRunning(true);
  }, [runCallback, delay, clear]);

  const reset = useCallback(() => {
    start();
  }, [start]);

  return { start, reset, clear, isRunning };
}
